import {otsBaseApi} from "../../ots-api";
import {CustomFieldProfile, CustomFieldProfileCatalogItem} from "../../../model/custom-field-profile";

const ALL_TAG_TYPES = [
  "CustomFieldProfiles",
  "CompanyCustomFieldProfiles",
  "CustomFieldProfileCatalogItems",
  "CompanyCustomFieldProfileCatalogItems",
  "ObjectObjectChangeLogs",
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const customFieldProfileApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllCustomFieldProfiles: builder.query<
      CustomFieldProfile[],
      void
    >({
      query: () => ({
        url: `/custom-field-profiles/all`,
        method: 'GET',
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "CustomFieldProfiles", id} as const)),
            {type: "CustomFieldProfiles", id: "LIST"}
          ];
        } else {
          return [{type: "CustomFieldProfiles", id: "LIST"}];
        }
      },
    }),
    getCustomFieldProfilesByCompany: builder.query<
      CustomFieldProfile[],
      string
    >({
      query: (arg) => ({
        url: `/custom-field-profiles/company/${arg}`,
        method: 'GET',
      }),
      providesTags: (result, _error, arg) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "CustomFieldProfiles", id} as const)),
            {type: "CompanyCustomFieldProfiles", id: arg}
          ];
        } else {
          return [{type: "CompanyCustomFieldProfiles", id: arg}];
        }
      },
    }),
    getCustomFieldProfileItemCatalogByCompany: builder.query<
      CustomFieldProfileCatalogItem[],
      { companyId: string, objectType: string }
    >({
      query: ({companyId, objectType}) => ({
        url: `/custom-field-profiles/company/${companyId}/item-catalog`,
        method: 'GET',
        params: {
          objectType: objectType
        }
      }),
      providesTags: (result, _error, {companyId, objectType}) => {
        return [{type: "CompanyCustomFieldProfileCatalogItems", id: `${companyId}:${objectType}`}];
      },
    }),
    getCustomFieldProfileItemCatalogByProfile: builder.query<
      CustomFieldProfileCatalogItem[],
      string
    >({
      query: (arg) => ({
        url: `/custom-field-profiles/${arg}/item-catalog`,
        method: 'GET',
      }),
      providesTags: (result, _error, arg) => {
        return [{type: "CustomFieldProfileCatalogItems", id: arg}];
      },
    }),
    getCustomFieldProfile: builder.query<CustomFieldProfile, string>({
      query: (id) => ({
        url: `/custom-field-profiles/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "CustomFieldProfiles", id}]
    }),
    createCustomFieldProfile: builder.mutation<CustomFieldProfile, Partial<CustomFieldProfile>>({
      query: (mapping) => ({
        url: "/custom-field-profiles",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: (result) => {
        const tags: { type: TagType, id?: string }[] = []

        tags.push({type: "CustomFieldProfiles", id: "LIST"})

        if (result?.companyId) {
          tags.push({type: "CompanyCustomFieldProfiles", id: result.companyId})
        }

        return tags
      }
    }),
    updateCustomFieldProfile: builder.mutation<CustomFieldProfile, Partial<CustomFieldProfile>>({
      query: (mapping) => ({
        url: "/custom-field-profiles",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "CustomFieldProfiles", id},
        {type: "ObjectObjectChangeLogs", id},
        {type: "CompanyCustomFieldProfiles"},
      ]
    }),
    deleteCustomFieldProfile: builder.mutation<void, Partial<CustomFieldProfile>>({
      query: (mapping) => ({
        url: "/custom-field-profiles",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "CustomFieldProfiles", id},
        {type: "CompanyCustomFieldProfiles"},
      ]
    }),

  })
});

export const {
  useGetAllCustomFieldProfilesQuery,
  useGetCustomFieldProfilesByCompanyQuery,
  useGetCustomFieldProfileItemCatalogByCompanyQuery,
  useGetCustomFieldProfileItemCatalogByProfileQuery,
  useGetCustomFieldProfileQuery,
  useCreateCustomFieldProfileMutation,
  useUpdateCustomFieldProfileMutation,
  useDeleteCustomFieldProfileMutation,
} = customFieldProfileApi;
